import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "@/components/Modal";
import { Input } from "@/components/Input";
import { roleManagementApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";

interface NewRoleModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}
export const NewRoleModal = ({
    isOpen,
    onClose,
    onSuccess,
}: NewRoleModalProps) => {
    const { t } = useTranslation("global");
    const { showSuccess, showError } = useToast();

    const [tag, setTag] = useState("");
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");

    const [loading, setLoading] = useState(false);

    const [tagError, setTagError] = useState(false);
    const [nameError, setNameError] = useState(false);

    const tagRef = useRef<HTMLDivElement>(null);
    const nameRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isOpen) {
            setTag("");
            setName("");
            setDescription("");
            setTagError(false);
            setNameError(false);
        }
    }, [isOpen]);

    const handleSubmit = async () => {
        const missingTag = !tag.trim();
        const missingName = !name.trim();

        if (missingTag || missingName) {
            setTagError(missingTag);
            setNameError(missingName);

            showError(t("developments.modal_new.validation_required"));

            const firstMissing = missingTag ? tagRef : nameRef;
            firstMissing.current?.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });

            return;
        }

        setLoading(true);
        try {
            await roleManagementApi.create({
                tag,
                name,
                description,
            });
            showSuccess(t("roles.created"));
            onSuccess();
            onClose();
        } catch (error) {
            showError(
                error instanceof Error ? error.message : t("roles.error_create")
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={t("roles.new_title")}
            onSubmit={handleSubmit}
            submitLabel={t("common.save")}
            submittingLabel={t("common.saving")}
            cancelLabel={t("common.cancel")}
            isSubmitting={loading}
            maxWidth="md"
        >
            <div className="space-y-4 p-4">
                <div ref={tagRef}>
                    <Input
                        label={t("roles.tag")}
                        value={tag}
                        onChange={(e) => {
                            setTag(e.target.value);
                            if (tagError) setTagError(false);
                        }}
                        placeholder="Ej. admin, client"
                        required
                        className={
                            tagError ? "animate-error-blink rounded-xl" : ""
                        }
                    />
                </div>
                <div ref={nameRef}>
                    <Input
                        label={t("roles.name")}
                        value={name}
                        onChange={(e) => {
                            setName(e.target.value);
                            if (nameError) setNameError(false);
                        }}
                        required
                        className={
                            nameError ? "animate-error-blink rounded-xl" : ""
                        }
                    />
                </div>
                <Input
                    label={t("roles.description")}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                />
            </div>
        </Modal>
    );
};
